import maplibregl from 'maplibre-gl';
import type { Scene, MapAdapter } from '../types';

const TITILER  = 'https://titiler-centinela-746476722093.us-central1.run.app';
const FIRE_COG = 'gs://centinela-test-data/openclaw/fire/fire_frequency_2000_2025.tif';

const BASE = `${TITILER}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}?url=${encodeURIComponent(FIRE_COG)}`;

type FireLayerId = 'fire-frequency' | 'regime-shift' | 'dry-season';

interface FireLayerDef {
  id:      FireLayerId;
  srcId:   string;
  lyrId:   string;
  label:   string;
  desc:    string;
  tileUrl: string;
  opacity: number;
  legend:  { color: string; label: string }[];
}

const LAYERS: FireLayerDef[] = [
  {
    id:      'fire-frequency',
    srcId:   'fire-freq-src',
    lyrId:   'fire-freq-layer',
    label:   'Fire Frequency',
    desc:    'Total months burned per pixel across the full 25-year MODIS record (2000–2025). ' +
             'Low values across Sapi reflect infrequent fire relative to the surrounding biome.',
    tileUrl: `${BASE}&bidx=1&rescale=0,6&colormap_name=hot_r&resampling=bilinear`,
    opacity: 0.85,
    legend: [
      { color: '#ffffff', label: '0 burns'  },
      { color: '#ff6600', label: '3 burns'  },
      { color: '#1a0000', label: '6 burns'  },
    ],
  },
  {
    id:      'regime-shift',
    srcId:   'regime-shift-src',
    lyrId:   'regime-shift-layer',
    label:   'Regime Shift',
    desc:    'Change in fire count: 2020–2025 minus 2000–2010 baseline. ' +
             'Blue = declining fire (improving), red = increasing fire pressure.',
    tileUrl: `${BASE}&expression=b7-b4&rescale=-3,3&colormap_name=rdbu`,
    opacity: 0.85,
    legend: [
      { color: '#2166ac', label: '−3 fewer'     },
      { color: '#f7f7f7', label: 'no change'    },
      { color: '#d73027', label: '+3 more fires' },
    ],
  },
  {
    id:      'dry-season',
    srcId:   'dry-season-src',
    lyrId:   'dry-season-layer',
    label:   'Dry Season',
    desc:    'Fire count restricted to dry-season months (June–September, b2 = 6–9). ' +
             'Isolates the high-intensity fire window most damaging to woody biomass.',
    tileUrl: `${BASE}&expression=(b2>5)*(b2<10)*b1&rescale=0,6&colormap_name=orrd`,
    opacity: 0.85,
    legend: [
      { color: '#fff7ec', label: '0 burns'    },
      { color: '#fc8d59', label: '3 burns'    },
      { color: '#7f0000', label: '6 burns'    },
    ],
  },
];

export class SceneFire implements Scene {
  readonly panel: HTMLElement;
  private mapLegend: HTMLElement | null = null;
  private activeId: FireLayerId = 'fire-frequency';
  private built = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly mapAdapter: MapAdapter,
  ) {
    this.panel = document.createElement('div');
    this.panel.className  = 'sm-panel';
    this.panel.dataset['scene'] = 'fire';
    this.panel.innerHTML = `
      <div class="sm-panel-header">
        <div class="sm-panel-title">Savanna Fire Management</div>
        <div class="sm-panel-sub">MODIS MCD64A1 · 500 m · 2000–2025</div>
      </div>

      <div class="sm-headline">
        Sapi holds a structural advantage in fire resilience — fewer perennial
        grasses and dispersed fuel loads reduce the risk of hot crown burns
        that threaten standing woody biomass.
      </div>

      <div class="sm-panel-narrative">
        Where grass-dominated savannas sustain intense annual fire, Sapi's
        mixed woodland mosaic and active management — seasonal fire breaks
        and early-detection monitoring — produce a fire regime that burns
        less frequently and at lower severity than the surrounding biome.
        The 25-year MODIS record shows a declining trend in fire frequency
        across the concession, consistent with improving landscape stewardship.
      </div>

      <div class="sm-fire-layer-btns" id="sm-fire-layer-btns">
        ${LAYERS.map(l => `
          <button class="sm-fire-layer-btn${l.id === 'fire-frequency' ? ' active' : ''}"
                  data-layer="${l.id}">${l.label}</button>`).join('')}
      </div>

      <div class="sm-fire-layer-desc" id="sm-fire-layer-desc"></div>

      <div class="sm-fire-stats">
        <div class="sm-fire-stat">
          <span>Record</span><strong>2000–2025</strong>
        </div>
        <div class="sm-fire-stat">
          <span>Resolution</span><strong>500 m MODIS</strong>
        </div>
        <div class="sm-fire-stat">
          <span>Max frequency</span><strong>6 burns</strong>
        </div>
      </div>`;

    root.appendChild(this.panel);

    this.mapLegend = document.createElement('div');
    this.mapLegend.className = 'sm-map-layer-legend';
    this.mapLegend.style.display = 'none';
    document.getElementById('sm-map')?.appendChild(this.mapLegend);
  }

  onEnter(): void {
    if (!this.built) {
      const btns = this.panel.querySelector<HTMLElement>('#sm-fire-layer-btns')!;
      btns.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.sm-fire-layer-btn');
        if (!btn) return;
        btns.querySelectorAll('.sm-fire-layer-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.showLayer(btn.dataset['layer'] as FireLayerId);
      });
      this.built = true;
    }
    this.showLayer(this.activeId);
  }

  onLeave(): void {
    const map = this.rawMap();
    LAYERS.forEach(l => {
      if (map.getLayer(l.lyrId)) map.removeLayer(l.lyrId);
      if (map.getSource(l.srcId)) map.removeSource(l.srcId);
    });
    if (this.mapLegend) this.mapLegend.style.display = 'none';
  }

  private showLayer(id: FireLayerId): void {
    this.activeId = id;
    const map = this.rawMap();

    // Remove all fire layers first
    LAYERS.forEach(l => {
      if (map.getLayer(l.lyrId)) map.removeLayer(l.lyrId);
      if (map.getSource(l.srcId)) map.removeSource(l.srcId);
    });

    const def = LAYERS.find(l => l.id === id)!;

    map.addSource(def.srcId, { type: 'raster', tiles: [def.tileUrl], tileSize: 256 });
    // Insert before first symbol layer so map labels remain on top
    const firstSymbol = map.getStyle().layers?.find(l => l.type === 'symbol')?.id;
    map.addLayer(
      { id: def.lyrId, type: 'raster', source: def.srcId, paint: { 'raster-opacity': def.opacity } },
      firstSymbol,
    );

    // Update inline description
    const descEl = this.panel.querySelector<HTMLElement>('#sm-fire-layer-desc')!;
    descEl.textContent = def.desc;

    // Update floating map legend
    if (this.mapLegend) {
      this.mapLegend.innerHTML =
        `<div class="sm-map-legend-title">${def.label}</div>` +
        def.legend.map(s =>
          `<div class="sm-map-legend-row">
            <span class="sm-map-legend-swatch" style="background:${s.color};border:1px solid rgba(255,255,255,0.15)"></span>
            <span class="sm-map-legend-label">${s.label}</span>
          </div>`
        ).join('');
      this.mapLegend.style.display = 'block';
    }
  }

  private rawMap(): maplibregl.Map {
    return (this.mapAdapter as unknown as { map: maplibregl.Map }).map;
  }
}
